import React, { useState } from 'react';
import { FileText, Truck, CheckSquare, BarChart2, Plus, Printer, RefreshCw, Mail, MessageCircle, ScanLine, QrCode, X } from 'lucide-react';

// ============================================================================
// HELPERS & COMPONENTS
// ============================================================================

// Simple Code 128-like Barcode Generator (SVG)
const Barcode = ({ value }) => {
  if (!value) return null;
  // Mocking a visual barcode pattern based on the string hash
  const bars = value.split('').map((char, i) => {
      const width = (char.charCodeAt(0) % 3) + 1;
      return <rect key={i} x={i * 4} y="0" width={width} height="40" fill="black" />;
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width={value.length * 4 + 10} height="40">{bars}</svg>
        <span style={{ fontSize: '10px', fontFamily: 'monospace', letterSpacing: '2px' }}>{value}</span>
    </div>
  );
};

const SubTabBtn = ({ label, active, onClick, icon: Icon }) => (
  <button 
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
      fontSize: '14px', fontWeight: 'bold', border: 'none', borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
      background: active ? '#eff6ff' : 'transparent', color: active ? '#1d4ed8' : '#64748b', cursor: 'pointer', transition: 'all 0.2s'
    }}
  >
    {Icon && <Icon size={16} />}
    {label}
  </button>
);

const Section = ({ title, children, action }) => (
  <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
        <h3 style={{ fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase', color: '#64748b', margin: 0 }}>{title}</h3>
        {action}
    </div>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type="text", options, style={}, onKeyDown }) => (
  <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '10px', flex:1, ...style }}>
    <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', marginBottom: '5px' }}>{label}</label>
    {options ? (
      <select value={value} onChange={onChange} style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={value} onChange={onChange} onKeyDown={onKeyDown} style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px' }} />
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ChallanModule({ db, setDb }) {
  const [activeTab, setActiveTab] = useState('REQUISITION');
  
  // DATA
  const [requisitions, setRequisitions] = useState(db.requisitions || []);
  const [challans, setChallans] = useState(db.challans || []);
  
  // FORMS
  const [reqForm, setReqForm] = useState({ id: null, reqNo: `REQ-${Date.now()}`, date: new Date().toISOString().split('T')[0], dept: '', requestor: '', status: 'Pending', items: [] });
  const [reqItem, setReqItem] = useState({ desc: '', qty: '', unit: 'Pcs', remarks: '' });

  const [dcForm, setDcForm] = useState({ id: null, dcNo: `DC-${Date.now()}`, date: new Date().toISOString().split('T')[0], type: 'External', returnable: 'Non-Returnable', receiver: '', address: '', vehicle: '', status: 'Pending', items: [] });
  const [dcItem, setDcItem] = useState({ desc: '', hsn: '', qty: '', rate: '', amount: '' });

  // SCANNED DATA VIEW
  const [scanQuery, setScanQuery] = useState('');
  const [scannedDoc, setScannedDoc] = useState(null);

  // RECONCILIATION FILTER
  const [reconPeriod, setReconPeriod] = useState('Month');

  // --- ACTIONS: REQUISITION ---
  const addReqItem = () => {
    if(!reqItem.desc || !reqItem.qty) return alert("Item Details Required");
    setReqForm(prev => ({ ...prev, items: [...prev.items, { ...reqItem, id: Date.now() }] }));
    setReqItem({ desc: '', qty: '', unit: 'Pcs', remarks: '' });
  };

  const submitReq = () => {
    if(reqForm.items.length === 0) return alert("Add items first");
    const newReqs = [reqForm, ...requisitions];
    setRequisitions(newReqs);
    setDb(prev => ({ ...prev, requisitions: newReqs }));
    setReqForm({ id: null, reqNo: `REQ-${Date.now()}`, date: new Date().toISOString().split('T')[0], dept: '', requestor: '', status: 'Pending', items: [] });
    alert("Requisition Generated!");
  };

  // --- ACTIONS: CHALLAN (with Barcode & Timestamp) ---
  const addDcItem = () => {
    if(!dcItem.desc || !dcItem.qty) return alert("Item Details Required");
    const amt = (parseFloat(dcItem.qty) * parseFloat(dcItem.rate || 0)).toFixed(2);
    setDcForm(prev => ({ ...prev, items: [...prev.items, { ...dcItem, amount: amt, id: Date.now() }] }));
    setDcItem({ desc: '', hsn: '', qty: '', rate: '', amount: '' });
  };

  const submitDc = () => {
    if(dcForm.items.length === 0) return alert("Add items first");
    
    // Generate Security Features
    const finalDc = { 
        ...dcForm, 
        barcode: `DC-${Math.floor(Math.random()*100000)}`, // Unique Barcode
        timestampCode: Date.now().toString(36).toUpperCase(), // Verification Code
        createdAt: new Date().toISOString() 
    };

    const newDcs = [finalDc, ...challans];
    setChallans(newDcs);
    setDb(prev => ({ ...prev, challans: newDcs }));
    setDcForm({ id: null, dcNo: `DC-${Date.now()}`, date: new Date().toISOString().split('T')[0], type: 'External', returnable: 'Non-Returnable', receiver: '', address: '', vehicle: '', status: 'Pending', items: [] });
    alert("Delivery Challan Generated with Security Codes!");
  };

  // --- SCANNING FUNCTION ---
  const handleScan = (e) => {
      if(e.key === 'Enter') {
          const found = challans.find(c => c.barcode === scanQuery || c.dcNo === scanQuery);
          if(found) {
              setScannedDoc(found);
              setScanQuery('');
          } else {
              alert("No Challan Found for this Barcode!");
          }
      }
  };

  // --- NOTIFICATIONS ---
  const sendNotification = (type, doc) => {
      const subject = `Approval Required: ${doc.dcNo || doc.reqNo}`;
      const body = `Dear Manager,%0D%0A%0D%0APlease review and approve the following document:%0D%0AID: ${doc.dcNo || doc.reqNo}%0D%0AType: ${type}%0D%0ADate: ${doc.date}%0D%0A%0D%0AClick here to approve: https://faircm-erp.com/approve/${doc.id}%0D%0A(Simulation Link)`;
      
      if(type === 'MAIL') {
          window.open(`mailto:manager@company.com?subject=${subject}&body=${body}`);
      } else {
          window.open(`https://wa.me/?text=${subject} - ${body}`);
      }
  };

  // --- APPROVALS & ISSUANCE ---
  const handleApproval = (type, id, status) => {
    if(type === 'REQ') {
        const updated = requisitions.map(r => r.id === id ? { ...r, status } : r);
        setRequisitions(updated);
        setDb(prev => ({ ...prev, requisitions: updated }));
    } else {
        const updated = challans.map(c => c.id === id ? { ...c, status } : c);
        setChallans(updated);
        setDb(prev => ({ ...prev, challans: updated }));
    }
  };

  const handleIssue = (req) => {
      let invUpdated = [...db.materials];
      let insufficient = false;

      req.items.forEach(item => {
          const matIndex = invUpdated.findIndex(m => m.Roll_ID === item.desc || m.Style_Name === item.desc);
          if(matIndex > -1) {
             const currentBal = parseFloat(invUpdated[matIndex].Balance_Qty);
             const reqQty = parseFloat(item.qty);
             if(currentBal >= reqQty) {
                 invUpdated[matIndex].Balance_Qty = currentBal - reqQty;
                 invUpdated[matIndex].Issue_Qty = (parseFloat(invUpdated[matIndex].Issue_Qty) || 0) + reqQty;
             } else {
                 insufficient = true;
             }
          }
      });

      if(insufficient) return alert("Insufficient Stock for some items!");
      
      const updatedReqs = requisitions.map(r => r.id === req.id ? { ...r, status: 'Issued' } : r);
      setRequisitions(updatedReqs);
      setDb(prev => ({ ...prev, materials: invUpdated, requisitions: updatedReqs }));
      alert("Items Issued & Inventory Updated!");
  };

  // --- RETURNABLE FOLLOWUP ---
  const closeReturnable = (id) => {
      const updated = challans.map(c => c.id === id ? { ...c, status: 'Returned' } : c);
      setChallans(updated);
      setDb(prev => ({ ...prev, challans: updated }));
  };

  // --- STATS ---
  const getStats = () => {
      const now = new Date();
      const filterFn = (d) => {
          const date = new Date(d);
          if(reconPeriod === 'Day') return date.toDateString() === now.toDateString();
          if(reconPeriod === 'Week') return (now - date) / (1000 * 60 * 60 * 24) <= 7;
          if(reconPeriod === 'Month') return date.getMonth() === now.getMonth();
          return date.getFullYear() === now.getFullYear();
      };

      const reqs = requisitions.filter(r => filterFn(r.date));
      const dcs = challans.filter(c => filterFn(c.date));

      return {
          reqCount: reqs.length,
          reqIssued: reqs.filter(r => r.status === 'Issued').length,
          dcCount: dcs.length,
          dcApproved: dcs.filter(c => c.status === 'Approved').length,
          itemsIssued: reqs.filter(r => r.status === 'Issued').reduce((acc, r) => acc + r.items.reduce((a,i)=>a+parseFloat(i.qty),0), 0),
          itemsSent: dcs.filter(c => c.status === 'Approved').reduce((acc, c) => acc + c.items.reduce((a,i)=>a+parseFloat(i.qty),0), 0)
      };
  };
  const stats = getStats();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: 'white', padding: '0 20px', alignItems:'center' }}>
        <SubTabBtn label="Requisition" active={activeTab === 'REQUISITION'} onClick={() => setActiveTab('REQUISITION')} icon={FileText} />
        <SubTabBtn label="Delivery Challan" active={activeTab === 'CHALLAN'} onClick={() => setActiveTab('CHALLAN')} icon={Truck} />
        <SubTabBtn label="RGP Tracker" active={activeTab === 'RGP'} onClick={() => setActiveTab('RGP')} icon={RefreshCw} />
        <SubTabBtn label="Approvals" active={activeTab === 'APPROVALS'} onClick={() => setActiveTab('APPROVALS')} icon={CheckSquare} />
        <SubTabBtn label="Reconciliation" active={activeTab === 'RECONCILIATION'} onClick={() => setActiveTab('RECONCILIATION')} icon={BarChart2} />
        
        {/* SCANNER */}
        <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:'10px'}}>
            <ScanLine size={16} color="#64748b"/>
            <input 
                placeholder="Scan Barcode / QR..." 
                value={scanQuery} 
                onChange={e=>setScanQuery(e.target.value)} 
                onKeyDown={handleScan}
                style={{padding:'6px', borderRadius:'4px', border:'1px solid #cbd5e1', fontSize:'12px', width:'200px'}} 
            />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        
        {/* SCANNED DOCUMENT MODAL VIEW */}
        {scannedDoc && (
            <div style={{marginBottom:'20px', background:'#f0f9ff', padding:'20px', borderRadius:'8px', border:'1px solid #0ea5e9'}}>
                <div style={{display:'flex', justifyContent:'space-between'}}>
                    <h3 style={{margin:0, color:'#0369a1'}}>Scanned Document Details</h3>
                    <button onClick={()=>setScannedDoc(null)} style={{background:'none', border:'none', cursor:'pointer'}}><X size={18}/></button>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'15px', marginTop:'15px'}}>
                    <div><strong>DC No:</strong> {scannedDoc.dcNo}</div>
                    <div><strong>Date:</strong> {scannedDoc.date}</div>
                    <div><strong>Receiver:</strong> {scannedDoc.receiver}</div>
                    <div><strong>Status:</strong> {scannedDoc.status}</div>
                    <div><strong>Barcode:</strong> {scannedDoc.barcode}</div>
                    <div><strong>Timestamp:</strong> <span style={{fontFamily:'monospace'}}>{scannedDoc.timestampCode}</span></div>
                </div>
                <table style={{width:'100%', marginTop:'15px', fontSize:'12px', background:'white'}}>
                    <thead><tr><th style={{textAlign:'left', padding:'5px'}}>Item</th><th>Qty</th></tr></thead>
                    <tbody>{scannedDoc.items.map((i,x)=><tr key={x}><td>{i.desc}</td><td style={{textAlign:'center'}}>{i.qty}</td></tr>)}</tbody>
                </table>
            </div>
        )}

        {/* --- 1. REQUISITION SLIP --- */}
        {activeTab === 'REQUISITION' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Section title="Create Material Requisition">
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Input label="Req No" value={reqForm.reqNo} onChange={e=>setReqForm({...reqForm, reqNo: e.target.value})} />
                        <Input label="Date" value={reqForm.date} onChange={e=>setReqForm({...reqForm, date: e.target.value})} type="date" />
                        <Input label="Department" value={reqForm.dept} onChange={e=>setReqForm({...reqForm, dept: e.target.value})} />
                    </div>
                    <Input label="Requestor Name" value={reqForm.requestor} onChange={e=>setReqForm({...reqForm, requestor: e.target.value})} />
                    
                    <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                        <h4 style={{ fontSize: '12px', margin: '0 0 10px 0' }}>Add Items</h4>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'end' }}>
                            <div style={{flex:2}}><Input label="Item Description / Roll ID" value={reqItem.desc} onChange={e=>setReqItem({...reqItem, desc: e.target.value})} /></div>
                            <div style={{flex:1}}><Input label="Qty" value={reqItem.qty} onChange={e=>setReqItem({...reqItem, qty: e.target.value})} type="number" /></div>
                            <div style={{flex:1}}><Input label="Unit" value={reqItem.unit} onChange={e=>setReqItem({...reqItem, unit: e.target.value})} /></div>
                            <button onClick={addReqItem} style={{ marginBottom: '10px', padding: '8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><Plus size={16} /></button>
                        </div>
                    </div>

                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginTop: '15px' }}>
                        <thead><tr style={{ background: '#e2e8f0' }}><th style={{ padding: '8px', textAlign: 'left' }}>Item</th><th>Qty</th><th>Unit</th></tr></thead>
                        <tbody>
                            {reqForm.items.map((i, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '8px' }}>{i.desc}</td>
                                    <td style={{ textAlign: 'center' }}>{i.qty}</td>
                                    <td style={{ textAlign: 'center' }}>{i.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={submitReq} style={{ width: '100%', marginTop: '20px', padding: '10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>GENERATE SLIP</button>
                </Section>

                <Section title="Recent Requisitions">
                    {requisitions.slice(0,10).map(r => (
                        <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#2563eb' }}>{r.reqNo}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>{r.date} | {r.dept} | {r.requestor}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: r.status==='Pending'?'#fef3c7':r.status==='Approved'?'#dbeafe':'#dcfce7', color: r.status==='Pending'?'#d97706':r.status==='Approved'?'#1e40af':'#166534' }}>{r.status}</span>
                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{r.items.length} Items</div>
                            </div>
                        </div>
                    ))}
                </Section>
            </div>
        )}

        {/* --- 2. DELIVERY CHALLAN --- */}
        {activeTab === 'CHALLAN' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Section title="Create Delivery Challan">
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Input label="DC No" value={dcForm.dcNo} onChange={e=>setDcForm({...dcForm, dcNo: e.target.value})} />
                        <Input label="Date" value={dcForm.date} onChange={e=>setDcForm({...dcForm, date: e.target.value})} type="date" />
                        <Input label="Type" value={dcForm.type} onChange={e=>setDcForm({...dcForm, type: e.target.value})} options={['Internal', 'External']} />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Input label="Receiver Name" value={dcForm.receiver} onChange={e=>setDcForm({...dcForm, receiver: e.target.value})} />
                        <Input label="Vehicle No" value={dcForm.vehicle} onChange={e=>setDcForm({...dcForm, vehicle: e.target.value})} />
                        <Input label="Category" value={dcForm.returnable} onChange={e=>setDcForm({...dcForm, returnable: e.target.value})} options={['Returnable', 'Non-Returnable']} />
                    </div>
                    <Input label="Address / Dept" value={dcForm.address} onChange={e=>setDcForm({...dcForm, address: e.target.value})} />

                    <div style={{ background: '#f1f5f9', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                        <h4 style={{ fontSize: '12px', margin: '0 0 10px 0' }}>Add Challan Items</h4>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'end' }}>
                            <div style={{flex:2}}><Input label="Description" value={dcItem.desc} onChange={e=>setDcItem({...dcItem, desc: e.target.value})} /></div>
                            <div style={{flex:1}}><Input label="Qty" value={dcItem.qty} onChange={e=>setDcItem({...dcItem, qty: e.target.value})} type="number" /></div>
                            <div style={{flex:1}}><Input label="Rate" value={dcItem.rate} onChange={e=>setDcItem({...dcItem, rate: e.target.value})} type="number" /></div>
                            <button onClick={addDcItem} style={{ marginBottom: '10px', padding: '8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><Plus size={16} /></button>
                        </div>
                    </div>

                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginTop: '15px' }}>
                        <thead><tr style={{ background: '#e2e8f0' }}><th style={{ padding: '8px', textAlign: 'left' }}>Item</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
                        <tbody>
                            {dcForm.items.map((i, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '8px' }}>{i.desc}</td>
                                    <td style={{ textAlign: 'center' }}>{i.qty}</td>
                                    <td style={{ textAlign: 'center' }}>{i.rate}</td>
                                    <td style={{ textAlign: 'center' }}>{i.amount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={submitDc} style={{ width: '100%', marginTop: '20px', padding: '10px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>GENERATE CHALLAN & BARCODE</button>
                </Section>

                <Section title="Recent Challans">
                    {challans.slice(0,10).map(c => (
                        <div key={c.id} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#2563eb' }}>{c.dcNo}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>{c.date} | To: {c.receiver}</div>
                                <div style={{ fontSize: '10px', color: '#000', marginTop:'5px', fontFamily:'monospace' }}>{c.timestampCode}</div>
                            </div>
                            <div style={{ textAlign: 'right', display:'flex', flexDirection:'column', alignItems:'flex-end' }}>
                                <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', background: c.status==='Pending'?'#fef3c7':c.status==='Approved'?'#dcfce7':'#fee2e2', color: c.status==='Pending'?'#d97706':c.status==='Approved'?'#166534':'#b91c1c' }}>{c.status}</span>
                                <div style={{marginTop:'5px'}}><Barcode value={c.barcode} /></div>
                            </div>
                        </div>
                    ))}
                </Section>
            </div>
        )}

        {/* --- 3. RGP TRACKER (Returnable Goods) --- */}
        {activeTab === 'RGP' && (
            <Section title="Returnable Goods Tracker">
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: '#f8fafc', borderBottom:'2px solid #e2e8f0' }}><th style={{padding:'10px', textAlign:'left'}}>DC No</th><th>Date</th><th>Receiver</th><th>Days Open</th><th>Status</th><th>Action</th></tr></thead>
                    <tbody>
                        {challans.filter(c => c.returnable === 'Returnable').map(c => {
                            const daysOpen = Math.floor((new Date() - new Date(c.date)) / (1000 * 60 * 60 * 24));
                            return (
                                <tr key={c.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{padding:'10px', fontWeight:'bold'}}>{c.dcNo}</td>
                                    <td>{c.date}</td>
                                    <td>{c.receiver}</td>
                                    <td style={{color: daysOpen > 15 ? 'red' : 'black'}}>{daysOpen} Days</td>
                                    <td><span style={{padding:'2px 6px', borderRadius:'4px', background: c.status==='Returned'?'#dcfce7':'#fef3c7', fontSize:'11px'}}>{c.status}</span></td>
                                    <td>
                                        {c.status !== 'Returned' && (
                                            <button onClick={()=>closeReturnable(c.id)} style={{background:'#2563eb', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>Mark Returned</button>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </Section>
        )}

        {/* --- 4. APPROVALS --- */}
        {activeTab === 'APPROVALS' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <Section title="Pending Requisitions">
                    {requisitions.filter(r => r.status === 'Pending').map(r => (
                        <div key={r.id} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '15px', marginBottom: '10px', background: '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <span style={{ fontWeight: 'bold' }}>{r.reqNo}</span>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>{r.requestor}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={()=>handleApproval('REQ', r.id, 'Approved')} style={{ flex: 1, padding: '5px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Approve</button>
                                <button onClick={()=>sendNotification('MAIL', r)} style={{ padding: '5px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><Mail size={16}/></button>
                                <button onClick={()=>sendNotification('WHATSAPP', r)} style={{ padding: '5px', background: '#25D366', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><MessageCircle size={16}/></button>
                            </div>
                        </div>
                    ))}
                </Section>

                <Section title="Pending Challans">
                    {challans.filter(c => c.status === 'Pending').map(c => (
                        <div key={c.id} style={{ border: '1px solid #e2e8f0', borderRadius: '4px', padding: '15px', marginBottom: '10px', background: '#fff' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <span style={{ fontWeight: 'bold' }}>{c.dcNo}</span>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>To: {c.receiver}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={()=>handleApproval('DC', c.id, 'Approved')} style={{ flex: 1, padding: '5px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Approve</button>
                                <button onClick={()=>sendNotification('MAIL', c)} style={{ padding: '5px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><Mail size={16}/></button>
                                <button onClick={()=>sendNotification('WHATSAPP', c)} style={{ padding: '5px', background: '#25D366', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><MessageCircle size={16}/></button>
                            </div>
                        </div>
                    ))}
                </Section>
            </div>
        )}

        {/* --- 5. RECONCILIATION --- */}
        {activeTab === 'RECONCILIATION' && (
            <Section title="Reconciliation & Summary">
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    {['Day', 'Week', 'Month', 'Year'].map(p => (
                        <button key={p} onClick={()=>setReconPeriod(p)} style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #cbd5e1', background: reconPeriod===p ? '#2563eb' : 'white', color: reconPeriod===p ? 'white' : '#64748b', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>{p}</button>
                    ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                    <div style={{ background: '#eff6ff', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2563eb' }}>{stats.reqCount}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold' }}>TOTAL REQUISITIONS</div>
                    </div>
                    <div style={{ background: '#f0fdf4', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#16a34a' }}>{stats.itemsIssued}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold' }}>ITEMS ISSUED (Qty)</div>
                    </div>
                    <div style={{ background: '#fff7ed', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ea580c' }}>{stats.dcApproved}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold' }}>CHALLANS SENT</div>
                    </div>
                    <div style={{ background: '#fef2f2', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc2626' }}>{stats.itemsSent}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 'bold' }}>ITEMS DISPATCHED (Qty)</div>
                    </div>
                </div>
            </Section>
        )}

      </div>
    </div>
  );
}